import { LayoutSizeContext } from 'App';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { addViewportResizeListeners } from 'utils/layout/viewportResize.utils';

const GRID_GAP_PX = 10;
const MIN_SIZE_PX = 1;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function normalizeSpan(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 1;
}

export default function LegacyWidgetLayoutAdapter({
  dimensions,
  children,
  layoutPreset = 'default',
  clipOverflow = false,
}) {
  const parentLayout = useContext(LayoutSizeContext);
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    let frameId = null;

    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      const width = Math.max(MIN_SIZE_PX, Math.floor(rect.width));
      const height = Math.max(MIN_SIZE_PX, Math.floor(rect.height));

      setContainerSize((prev) => {
        if (prev && Math.abs(prev.width - width) < 1 && Math.abs(prev.height - height) < 1) {
          return prev;
        }
        return { width, height };
      });
    };

    const scheduleUpdate = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updateSize();
      });
    };

    scheduleUpdate();

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        scheduleUpdate();
      });
      observer.observe(node);
    }

    const removeViewportResizeListeners = addViewportResizeListeners(scheduleUpdate);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      if (observer) {
        observer.disconnect();
      }
      removeViewportResizeListeners();
    };
  }, []);

  const adaptedLayout = useMemo(() => {
    if (!containerSize || !parentLayout) {
      return parentLayout;
    }

    const cols = normalizeSpan(dimensions?.[0]);
    const rows = normalizeSpan(dimensions?.[1]);

    const widgetWidth = Math.max(
      MIN_SIZE_PX,
      (containerSize.width - GRID_GAP_PX * (cols - 1)) / cols,
    );
    const widgetHeight = Math.max(
      MIN_SIZE_PX,
      (containerSize.height - GRID_GAP_PX * (rows - 1)) / rows,
    );

    const widthRatio =
      Number.isFinite(parentLayout.widgetWidth) && parentLayout.widgetWidth > 0
        ? widgetWidth / parentLayout.widgetWidth
        : 1;
    const heightRatio =
      Number.isFinite(parentLayout.widgetHeight) && parentLayout.widgetHeight > 0
        ? widgetHeight / parentLayout.widgetHeight
        : 1;
    const contentRatio =
      layoutPreset === 'forecastFit'
        ? Math.max(0.25, Math.min(widthRatio, heightRatio))
        : widthRatio;

    const baseFontSize = Number.isFinite(parentLayout.baseFontSize)
      ? parentLayout.baseFontSize
      : 14;
    const parentScaleMult =
      Number.isFinite(parentLayout.scaleMult) && parentLayout.scaleMult > 0
        ? parentLayout.scaleMult
        : 1;

    return {
      ...parentLayout,
      width: containerSize.width,
      height: containerSize.height,
      widgetWidth,
      doubleWidgetWidth: widgetWidth * 2 + GRID_GAP_PX,
      widgetHeight,
      doubleWidgetHeight: widgetHeight * 2 + GRID_GAP_PX,
      baseFontSize: clamp(baseFontSize * contentRatio, 10, 30),
      scaleMult: Math.max(0.25, parentScaleMult * contentRatio),
    };
  }, [containerSize, dimensions, layoutPreset, parentLayout]);

  const content =
    typeof children === 'function'
      ? children({
          containerSizePx: containerSize,
          adaptedLayout,
        })
      : children;

  return (
    <div
      ref={containerRef}
      className="w-100 h-100"
      style={{
        minWidth: 0,
        minHeight: 0,
        overflow: clipOverflow ? 'hidden' : undefined,
        containerType: 'inline-size',
        '--widget-container-width': containerSize ? `${containerSize.width}px` : undefined,
        '--widget-container-height': containerSize ? `${containerSize.height}px` : undefined,
      }}
    >
      <LayoutSizeContext.Provider value={adaptedLayout}>{content}</LayoutSizeContext.Provider>
    </div>
  );
}
